import { useCallback } from 'react';

import { addRecentSearch, RECENT_SEARCH_KEY } from '@atlassian/search-common';

import type { ConnectorSource } from '../../common/constants/schemas/3p-config';
import { type SearchResultEventArguments } from '../../common/ui/quick-find/search-dialog-result';
import { onAdvancedSearchLinkSelected } from '../../common/utils/quick-find/events/advanced-search-link-selected';
import { onSearchDialogResultContextMenuOpened } from '../../common/utils/quick-find/events/search-dialog-result-context-menu-opened';
import { onSearchDialogResultHighlighted } from '../../common/utils/quick-find/events/search-dialog-result-highlighted';
import { onSearchDialogResultSelected } from '../../common/utils/quick-find/events/search-dialog-result-selected';
import { State } from '../../common/utils/quick-find/state';
import {
	useQuickFindActions,
	useQuickFindQuery,
	useUpdateRecentQueries,
} from '../../controllers/store/quick-find';
import { usePrimaryProduct, useUser } from '../store/bootstrap/accessors';

import { useQuickFindAnalytics, useQuickFindAttributes } from './analytics';
import { useIntentDetectionVitalStats } from './use-intent-detection-vitalstats';

export function getActionSubjectId(
	state: State,
):
	| ''
	| 'preQuerySearchResults'
	| 'cachedResults'
	| 'postQuerySearchResults'
	| 'preQueryLoadingSearchResults' {
	switch (state) {
		case State.PREQUERY_RESULTS:
			return 'preQuerySearchResults';
		case State.PREQUERY_LOADING:
			return 'preQueryLoadingSearchResults';
		case State.POSTQUERY_LOADING:
			return 'cachedResults';
		case State.POSTQUERY_RESULTS:
			return 'postQuerySearchResults';
		default:
			return '';
	}
}

type onClickOptions = {
	addCurrentQueryAsRecentQuery?: boolean;
};

export const isOpenInNewTabInteraction: (
	event?: React.MouseEvent<HTMLElement>,
	data?: SearchResultEventArguments,
) => boolean = (
	event?: React.MouseEvent<HTMLElement>,
	data?: SearchResultEventArguments,
): boolean => {
	if (!event) {
		return false;
	}

	// cmd/ctrl/shift click typically indicates the user intends to open in a new tab/window.
	const isModifiedClick = event.ctrlKey || event.metaKey || event.shiftKey;

	// Middle click (f.e. scroll-wheel) is a common "open in new tab" interaction.
	const isAuxClick = event.type === 'auxclick' || data?.isAuxClick === true;

	return isModifiedClick || isAuxClick;
};

export type QuickFindResultEvent = {
	event?: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>;
	data: SearchResultEventArguments;
	state: State;
	additionalAnalyticsAttributes?: Record<string, any> & {
		is3pLink?: boolean;
		is3PSmartLink?: boolean;
		connectorSource?: ConnectorSource;
	};
	options?: onClickOptions;
};

export const useQuickFindResultOnClick: () => (props: QuickFindResultEvent) => void = () => {
	const [user] = useUser();
	const [primaryProduct] = usePrimaryProduct();
	const quickFindQuery = useQuickFindQuery();
	const updateRecentQueries = useUpdateRecentQueries();

	const { setQuickFindOpen } = useQuickFindActions();

	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();

	const { abortTask: abortIntentDetectionTask, isTaskActive: isIntentDetectionTaskActive } =
		useIntentDetectionVitalStats();

	return useCallback(
		(props: QuickFindResultEvent) => {
			const { event, data, state, additionalAnalyticsAttributes, options } = props;

			// VitalStats: Abort intent detection UX task if user clicks on a search result
			if (isIntentDetectionTaskActive()) {
				abortIntentDetectionTask('result_selected');
			}

			const { addCurrentQueryAsRecentQuery = true } = options || {};

			const newTab = isOpenInNewTabInteraction(event, data);
			if (!newTab) {
				setQuickFindOpen(false);
			}

			if (addCurrentQueryAsRecentQuery) {
				const trimmedInuptQuery = quickFindQuery.trim();
				if (trimmedInuptQuery !== '' && user && user.id && primaryProduct) {
					addRecentSearch(trimmedInuptQuery, user.id, primaryProduct, RECENT_SEARCH_KEY);
					updateRecentQueries?.();
				}
			}

			const actionSubjectId = getActionSubjectId(state);

			fireAnalyticsEvent(
				onSearchDialogResultSelected({
					actionSubjectId,
					attributes: {
						...commonAttributes,
						...additionalAnalyticsAttributes,
						...data,
					},
					nonPrivacySafeAttributes,
				}),
			);
		},
		[
			commonAttributes,
			fireAnalyticsEvent,
			quickFindQuery,
			nonPrivacySafeAttributes,
			primaryProduct,
			setQuickFindOpen,
			updateRecentQueries,
			user,
			isIntentDetectionTaskActive,
			abortIntentDetectionTask,
		],
	);
};

export const useQuickFindResultOnContextMenu: () => (props: QuickFindResultEvent) => void = () => {
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();

	return useCallback(
		(props: QuickFindResultEvent) => {
			const { data, state, additionalAnalyticsAttributes } = props;

			const actionSubjectId = getActionSubjectId(state);

			fireAnalyticsEvent(
				onSearchDialogResultContextMenuOpened({
					actionSubjectId,
					attributes: {
						...commonAttributes,
						...additionalAnalyticsAttributes,
						...data,
					},
					nonPrivacySafeAttributes,
				}),
			);
		},
		[commonAttributes, fireAnalyticsEvent, nonPrivacySafeAttributes],
	);
};

export const useQuickFindResultOnHighlighted: () => (props: QuickFindResultEvent) => void = () => {
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();

	return useCallback(
		(props: QuickFindResultEvent) => {
			const { data, state, additionalAnalyticsAttributes } = props;

			const actionSubjectId = getActionSubjectId(state);

			fireAnalyticsEvent(
				onSearchDialogResultHighlighted({
					actionSubjectId,
					attributes: {
						...commonAttributes,
						...additionalAnalyticsAttributes,
						...data,
					},
					nonPrivacySafeAttributes,
				}),
			);
		},
		[commonAttributes, fireAnalyticsEvent, nonPrivacySafeAttributes],
	);
};

export type QuickFindFooterEvent = {
	event:
		| React.MouseEvent<HTMLDivElement>
		| React.MouseEvent<HTMLButtonElement>
		| React.MouseEvent<HTMLAnchorElement, MouseEvent>;
	state: State;
	actionSubjectId: string;
	options?: onClickOptions;
};

export const useQuickFindFooterOnClick: () => (props: QuickFindFooterEvent) => void = () => {
	const [user] = useUser();
	const [primaryProduct] = usePrimaryProduct();
	const quickFindQuery = useQuickFindQuery();
	const updateRecentQueries = useUpdateRecentQueries();

	const { setQuickFindOpen } = useQuickFindActions();

	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();

	const { abortTask: abortIntentDetectionTask, isTaskActive: isIntentDetectionTaskActive } =
		useIntentDetectionVitalStats();

	return useCallback(
		(props: QuickFindFooterEvent) => {
			const { event, state, options, actionSubjectId } = props;

			// VitalStats: Abort intent detection UX task if user clicks footer link
			if (isIntentDetectionTaskActive()) {
				abortIntentDetectionTask('footer_link_selected');
			}

			const { addCurrentQueryAsRecentQuery = true } = options || {};

			const newTab = isOpenInNewTabInteraction(event);

			if (!newTab) {
				setQuickFindOpen(false);
			}

			if (addCurrentQueryAsRecentQuery) {
				const trimmedInputQuery = quickFindQuery.trim();
				if (trimmedInputQuery !== '' && user && user.id && primaryProduct) {
					addRecentSearch(trimmedInputQuery, user.id, primaryProduct, RECENT_SEARCH_KEY);
					updateRecentQueries?.();
				}
			}

			if (!primaryProduct) {
				return;
			}

			fireAnalyticsEvent(
				onAdvancedSearchLinkSelected({
					actionSubjectId,
					attributes: {
						...commonAttributes,
						trigger: 'click',
						newTab,
						isLoading: state === State.PREQUERY_LOADING || state === State.POSTQUERY_LOADING,
					},
					nonPrivacySafeAttributes,
				}),
			);
		},
		[
			commonAttributes,
			fireAnalyticsEvent,
			quickFindQuery,
			nonPrivacySafeAttributes,
			primaryProduct,
			setQuickFindOpen,
			updateRecentQueries,
			user,
			isIntentDetectionTaskActive,
			abortIntentDetectionTask,
		],
	);
};
